/* Size guide — the panel of size tables the product page opens from the header
 * of its size run.
 *
 * Why a file of its own rather than a {% javascript %} block in the snippet:
 * the same reason as the gallery, the cards and the bands. `shopify theme dev`
 * serves the compiled bundles truncated past a moving threshold, and a bundle
 * that does not parse registers nothing at all; files in `assets/` are served
 * whole. And a snippet's block only runs on pages that rendered that snippet,
 * which is exactly where this one is loaded from anyway.
 *
 * What it does, and it is little: the panel is a native modal `<dialog>`, so
 * the veil, Escape, the focus trap and the return of the focus to the opener
 * are the browser's work. This only opens it, closes it, and — the part that
 * matters — **turns the opener on**. The opener ships `disabled` so that a page
 * without JavaScript shows no control that leads nowhere, the rule this theme
 * follows everywhere.
 *
 * The opener lives in `product-form`, outside this element: it sits in the
 * header of the size run, which on a phone travels into a sheet of its own.
 * They are found in the document rather than passed to each other, which is why
 * the attribute below is the whole contract between them.
 *
 * Markup contract (what the script looks for in the DOM):
 *
 *   <size-guide> containing:
 *     [data-size-guide-dialog]  the <dialog> holding the tables
 *     [data-size-guide-close]   closes it
 *
 *   and anywhere in the document:
 *     [data-size-guide]         the opener, shipped `disabled`
 *
 * Class names are free: nothing here reads one.
 */

class SizeGuide extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-size-guide-dialog]');
    if (!this.dialog) return;

    this.querySelector('[data-size-guide-close]')?.addEventListener('click', () => this.dialog.close());

    /* A click landing on the dialog itself is a click on the veil: the content
       fills the box, as in the other panels of this theme. */
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    /* Last, once everything above is wired: the openers become real buttons. */
    this.openers = [...document.querySelectorAll('[data-size-guide]')];
    this.openers.forEach((opener) => {
      opener.disabled = false;
      opener.setAttribute('aria-haspopup', 'dialog');
      opener.addEventListener('click', () => this.dialog.showModal());
    });
  }
}

if (!customElements.get('size-guide')) {
  customElements.define('size-guide', SizeGuide);
}
